import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import client from '../api/client.ts';
import { ep } from '../api/endpoints.ts';

interface DecodedJwt {
  roles?: string | string[];
  exp: number;
  sub: string;
}

export interface CurrentUserContext {
  id?: number;
  email?: string;
  roles?: string[];
  tenantId?: number | null;
  internalUser?: boolean;
  tenantAdmin?: boolean;
  activeLocationId?: number | null;
  primaryLocationId?: number | null;
  effectiveMenuPermissions?: string[];
}

const CURRENT_USER_STORAGE_KEY = 'currentUserContext';

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

export const setSession = (accessToken: string) => {
  localStorage.setItem('accessToken', accessToken);
  try {
    const decoded: DecodedJwt = jwtDecode(accessToken);
    const rolesArr = normalizeStringArray(decoded.roles);
    localStorage.setItem(
      'user',
      JSON.stringify({ roles: rolesArr, email: decoded.sub }),
    );
  } catch {
    /* ignore */
  }
};

export const getStoredCurrentUserContext = (): CurrentUserContext | null => {
  try {
    const raw = localStorage.getItem(CURRENT_USER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      ...parsed,
      roles: normalizeStringArray(parsed.roles),
      effectiveMenuPermissions: normalizeStringArray(
        parsed.effectiveMenuPermissions,
      ),
    };
  } catch {
    return null;
  }
};

export const setCurrentUserContext = (context: CurrentUserContext | null) => {
  if (!context) {
    localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
    return;
  }
  localStorage.setItem(CURRENT_USER_STORAGE_KEY, JSON.stringify(context));
};

export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export const clearSession = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
  localStorage.removeItem('sa_tenant_id');
};

interface AuthCtx {
  loading: boolean;
  user: CurrentUserContext | null;
  currentUser: CurrentUserContext | null;
  isAuthenticated: () => boolean;
  hasMenuPermission: (menuKey: string, actionCode?: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasRole: (role: string) => boolean;
  refreshCurrentUser: () => Promise<CurrentUserContext | null>;
}

const AuthContext = createContext<AuthCtx>({
  loading: true,
  user: null,
  currentUser: null,
  isAuthenticated,
  hasMenuPermission: () => false,
  hasAnyRole: () => false,
  hasRole: () => false,
  refreshCurrentUser: async () => null,
});

async function fetchCurrentUserContext(
  explicitToken?: string,
): Promise<CurrentUserContext> {
  const token = explicitToken ?? localStorage.getItem('accessToken');
  if (!token) {
    throw new Error('Missing access token');
  }

  const response = await axios.get('/api/v1/users/current', {
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = response.data as CurrentUserContext;

  if (!data?.internalUser) {
    clearSession();
    throw new Error('Consola 3100 este rezervata echipei interne.');
  }

  const normalized: CurrentUserContext = {
    ...data,
    roles: normalizeStringArray(data.roles),
    effectiveMenuPermissions: normalizeStringArray(data.effectiveMenuPermissions),
  };
  setCurrentUserContext(normalized);
  return normalized;
}

export const hydrateCurrentUserContext = async (
  explicitToken?: string,
): Promise<CurrentUserContext> => fetchCurrentUserContext(explicitToken);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<CurrentUserContext | null>(
    getStoredCurrentUserContext(),
  );

  const refreshCurrentUser = async (): Promise<CurrentUserContext | null> => {
    try {
      const currentUser = await fetchCurrentUserContext();
      setUser(currentUser);
      return currentUser;
    } catch {
      setUser(null);
      throw new Error('Consola 3100 este rezervata echipei interne.');
    }
  };

  useEffect(() => {
    const bootstrapAuth = async () => {
      const path = window.location.pathname;

      if (path.startsWith('/login') && !isAuthenticated()) {
        clearSession();
        setUser(null);
        setLoading(false);
        return;
      }

      if (isAuthenticated()) {
        try {
          await refreshCurrentUser();
        } catch {
          clearSession();
        } finally {
          setLoading(false);
        }
        return;
      }

      if (document.cookie.includes('refresh=')) {
        client
          .post(ep.authRefresh, {})
          .then(async (response) => {
            setSession(response.data.accessToken);
            await refreshCurrentUser();
          })
          .catch(() => {
            clearSession();
            setUser(null);
          })
          .finally(() => setLoading(false));
        return;
      }

      setUser(null);
      setLoading(false);
    };

    bootstrapAuth();
  }, []);

  const hasMenuPermission = (menuKey: string, actionCode = 'VIEW'): boolean => {
    if (!menuKey) return true;
    const permissions = user?.effectiveMenuPermissions ?? [];
    return permissions.includes(`${menuKey}:${actionCode.toUpperCase()}`);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!roles.length) return true;
    const currentRoles = normalizeStringArray(user?.roles);
    const normalizedRoles = currentRoles.map((role) => role.toUpperCase());
    return roles.some((role) => {
      const expected = role.toUpperCase();
      return (
        normalizedRoles.includes(expected) ||
        normalizedRoles.includes(`ROLE_${expected}`)
      );
    });
  };

  const hasRole = (role: string): boolean => hasAnyRole([role]);

  return (
    <AuthContext.Provider
      value={{
        loading,
        user,
        currentUser: user,
        isAuthenticated,
        hasMenuPermission,
        hasAnyRole,
        hasRole,
        refreshCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const logout = async () => {
  try {
    await axios.post('/api/v1/auth/logout', {}, { withCredentials: true });
  } finally {
    clearSession();
  }
};

export const refreshToken = async (): Promise<string> => {
  const response = await axios.post('/api/v1/auth/refresh', {});
  const { accessToken } = response.data;
  setSession(accessToken);
  return accessToken;
};

export const deleteRefreshCookie = () => {
  document.cookie = 'refresh=; Path=/api/v1/auth/refresh; Max-Age=0';
};
